import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { type FormEvent, useState } from 'react';
import type { SettingListRow } from './singleTableListSupport';
import { formatSettingsRowDate, settingsListApiError } from './singleTableListSupport';
import { createSingleTableRow, updateSingleTableRow } from './singleTableApi';
import OtherSettingsListForm, { type OtherSettingsDraft } from './OtherSettingsListForm';
import SettingsCrudDialog from './SettingsCrudDialog';
import SettingsListDeleteButton from './SettingsListDeleteButton';
import SettingsListEditButton from './SettingsListEditButton';
import SettingsListSection, { SettingsEmptyTableRow } from './SettingsListSection';
import { useSingleTableList } from './useSingleTableList';

const headSx = { fontWeight: 600, bgcolor: 'action.hover' } as const;

export default function OtherSettingsList() {
  const { rows, loading, error, load, deleteById, deletingId } = useSingleTableList<SettingListRow>();
  const [draft, setDraft] = useState<OtherSettingsDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setFormError(null);
    setDraft({ mode: 'create', id: '', value: '', value_en: '', link: '', fileUrl: '' });
  };

  const openEdit = (row: SettingListRow) => {
    setFormError(null);
    setDraft({
      mode: 'edit',
      id: row.id,
      value: row.value ?? '',
      value_en: row.value_en ?? '',
      link: row.link ?? '',
      fileUrl: row.fileUrl ?? '',
    });
  };

  const closeForm = () => {
    setDraft(null);
    setFormError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setFormError(null);
    const id = draft.id.trim();
    const value = draft.value.trim();
    const value_en = draft.value_en.trim();
    const link = draft.link.trim();
    const fileUrl = draft.fileUrl.trim();
    if (!id) {
      setFormError('ID оруулна уу.');
      return;
    }
    if (!value) {
      setFormError('Утга (MN) оруулна уу.');
      return;
    }
    if (!value_en) {
      setFormError('Утга (EN) оруулна уу.');
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, string> = { id, value, value_en, link, fileUrl };
      if (draft.mode === 'create') {
        await createSingleTableRow(body);
      } else {
        await updateSingleTableRow(body);
      }
      closeForm();
      await load({ silent: true });
    } catch (err: unknown) {
      setFormError(
        settingsListApiError(err, draft.mode === 'create' ? 'Нэмэхэд алдаа гарлаа.' : 'Шинэчлэхэд алдаа гарлаа.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const dialogOpen = draft !== null;
  const dialogTitle = draft?.mode === 'create' ? 'Шинэ тохиргоо' : draft?.mode === 'edit' ? 'Тохиргоо засах' : '';

  return (
    <>
      <SettingsCrudDialog
        open={dialogOpen}
        title={dialogTitle}
        error={formError}
        saving={saving}
        onClose={closeForm}
        onSubmit={(e) => void handleSubmit(e)}
      >
        {draft && <OtherSettingsListForm draft={draft} onChange={setDraft} />}
      </SettingsCrudDialog>

      <SettingsListSection
        title="Бусад"
        subtitle="Нэмэлт тохиргооны мөрүүд"
        onAdd={openCreate}
        error={loading ? null : error}
        loading={loading}
        skeletonColumns={6}
      >
        <TableContainer sx={{ maxHeight: { xs: 400, md: 560 } }}>
          <Table size="small" stickyHeader aria-label="Бусад тохиргооны жагсаалт">
            <TableHead>
              <TableRow>
                <TableCell sx={headSx}>ID</TableCell>
                <TableCell sx={headSx}>Утга</TableCell>
                <TableCell sx={headSx}>Утга (EN)</TableCell>
                <TableCell sx={headSx}>Холбоос</TableCell>
                <TableCell sx={headSx}>Шинэчилсэн</TableCell>
                <TableCell sx={{ ...headSx, width: 1, whiteSpace: 'nowrap' }} align="right">
                  Үйлдэл
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <SettingsEmptyTableRow colSpan={6} />
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>{row.value}</TableCell>
                    <TableCell>{row.value_en}</TableCell>
                    <TableCell>
                      {row.link?.trim() ? (
                        <Link href={row.link} target="_blank" rel="noopener noreferrer" color="primary">
                          холбоос
                        </Link>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{formatSettingsRowDate(row.updated_at)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <SettingsListEditButton onEdit={() => openEdit(row)} />
                        <SettingsListDeleteButton
                          id={row.id}
                          deletingId={deletingId}
                          onDelete={deleteById}
                          itemLabel={`${row.id}: ${row.value}`}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsListSection>
    </>
  );
}
